import { useLayoutEffect, useRef, useState } from 'react';
import { type RefCallback } from 'react';

/**
 * Self-aware "does it fit" toggle for responsive toolbars — a JS alternative to a hardcoded
 * container-query breakpoint (`@[560px]/main`). Attach `ref` to the toolbar row; it reports
 * `collapsed = true` when the row can't fit its inline controls, and expands again once there's
 * room.
 *
 * The "natural width" is measured by briefly forcing the row to `max-content` and reading its
 * width (restored synchronously, before paint). That's what makes it reliable: `max-content`
 * ignores `flex-grow` (so a `grow` breadcrumb / left section doesn't stretch the measurement to
 * fill the toolbar) and un-clips overflow-hidden children (so a truncated breadcrumb still counts
 * as needing its full width).
 *
 * To decide when to expand again it caches the inline-vs-collapsed controls width difference at
 * collapse time, then expands as soon as `currentContent + inlineControls` fits — so it re-expands
 * both when the window grows and when the content shrinks (e.g. a "Delete" button disappearing on
 * deselect), with no stale threshold.
 *
 * It watches the *parent* with `ResizeObserver` (not the row — measuring forces the row's width,
 * which would otherwise feed back into the observer) and the row's content with `MutationObserver`
 * (so it reacts to buttons appearing/disappearing and breadcrumb text changing).
 */
export function useOverflowCollapse<T extends HTMLElement>() {
  // Callback ref, not `useRef`: the measured row can mount after the host (e.g. ingestion
  // shows a "select a folder" banner until a folder is picked). State makes the attach
  // re-run the effect below; a plain ref would leave it armed on null forever.
  const [el, setEl] = useState<T | null>(null);
  const [collapsed, setCollapsed] = useState(false);
  const neededAtCollapse = useRef(0);
  const controlsDelta = useRef(0);

  useLayoutEffect(() => {
    if (!el) {
      return;
    }

    const naturalWidth = () => {
      const previous = el.style.width;
      el.style.width = 'max-content';
      const width = el.scrollWidth;
      el.style.width = previous;
      return width;
    };

    let deltaMeasured = !collapsed;

    const measure = () => {
      const available = el.clientWidth;
      const needed = naturalWidth();

      if (!collapsed) {
        if (needed > available) {
          neededAtCollapse.current = needed;
          setCollapsed(true);
        }
        return;
      }

      // First measure after collapsing: `needed` now reflects the narrower collapsed controls,
      // so the gap from the pre-collapse width is how much the inline controls would add back.
      if (!deltaMeasured) {
        controlsDelta.current = neededAtCollapse.current - needed;
        deltaMeasured = true;
      }

      if (needed + controlsDelta.current <= available) {
        setCollapsed(false);
      }
    };

    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(el.parentElement ?? el);
    const mutationObserver = new MutationObserver(measure);
    mutationObserver.observe(el, { childList: true, subtree: true, characterData: true });
    measure();

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [el, collapsed]);

  return { ref: setEl as RefCallback<T>, collapsed };
}
